import React, { useCallback, useEffect, useMemo, useState } from "react";
import { graphCollectionsApi, mapFeedToGraphCollectionItems } from "lib/api/graphCollections";
import GraphCollectionFeed from "components/Graph/GraphCollectionFeed";
import { graphMessage, resolveGraphLocale } from "components/Graph/graphCopy";

const PAGE_SIZE = 12;

/**
 * Paginated graph collections feed. Each entry is rendered as a standard
 * collection EntityCard with the graph canvas pinned above the media area.
 * We deliberately do NOT wrap the feed in a single `ReactFlowProvider`: each
 * card provides its own provider so React Flow state stays isolated per-card.
 */
const GraphExplorerFeed: React.FC = () => {
  const locale = useMemo(
    () => resolveGraphLocale(typeof navigator !== "undefined" ? navigator.language : undefined),
    []
  );
  const [nextRequestOffset, setNextRequestOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [aggregated, setAggregated] = useState<ReturnType<typeof mapFeedToGraphCollectionItems>>(
    []
  );
  const [hasMore, setHasMore] = useState(false);

  const fetchPage = useCallback(
    async (offset: number, append: boolean) => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const data = await graphCollectionsApi.getFeed({ limit: PAGE_SIZE, offset });
        const mapped = mapFeedToGraphCollectionItems(data.items);
        setAggregated((prev) => (append ? [...prev, ...mapped] : mapped));
        setHasMore(Boolean(data.hasMore));
        setNextRequestOffset(data.offset + data.items.length);
      } catch {
        setLoadError(graphMessage(locale, "feedEmptySubtitle"));
      } finally {
        setIsLoading(false);
      }
    },
    [locale]
  );

  useEffect(() => {
    void fetchPage(0, false);
  }, [fetchPage]);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || isLoading) {
      return;
    }
    void fetchPage(nextRequestOffset, true);
  }, [fetchPage, hasMore, isLoading, nextRequestOffset]);

  return (
    <div className="graph-explorer-feed flex min-h-[calc(100vh-56px)] flex-col">
      {loadError ? (
        <div className="px-4 py-3 text-sm text-warning-red dark:text-warning-red-dark" role="alert">
          {loadError}
        </div>
      ) : null}
      <GraphCollectionFeed
        items={aggregated}
        isLoading={isLoading}
        hasNextPage={hasMore}
        onLoadMore={handleLoadMore}
        emptyTitle={graphMessage(locale, "feedEmptyTitle")}
        emptySubtitle={graphMessage(locale, "feedEmptySubtitle")}
        loadingLabel={graphMessage(locale, "feedLoading")}
      />
    </div>
  );
};

export default GraphExplorerFeed;
